import { useState } from "react";
import { Alert } from "@leylion/design-system";
import type { OrderWorkflow, Receipt } from "../api/cashier-types";
import { BRAND } from "../brand";
import { CashierClosedOrdersDrawer } from "./CashierClosedOrdersDrawer";

type Language = "ar" | "en";

type CashierTopBarV2Props = {
  language: Language;
  branchName: string;
  tenantName: string;
  roleName: string;
  shiftStatus: "Open" | "Closed" | null;
  shiftOpenedAt: string | null;
  closedOrderCount: number;
  openOrderCount: number;
  online: boolean;
  pendingSyncCount: number;
  queueRequiresReview?: boolean;
  queueErrorCode?: string | null;
  syncing: boolean;
  pendingApprovalCount: number;
  futureOrdersAvailable: boolean;
  futureOrdersEnabled: boolean;
  onOpenShift: () => void;
  onOpenClosedOrders: () => void;
  onOpenSavedOrders: () => void;
  onSync: () => void;
  onOpenFutureOrders: () => void;
  onOpenManagerApprovals?: () => void;
  onToggleLanguage: () => void;
  onSwitchContext: () => void;
  onLogout: () => void;
  onPrintClosedReceipt: (receipt: Receipt, mode: "payment" | "fiscal") => void;
  onCorrectClosedOrderCustomer?: (workflow: OrderWorkflow) => void;
  requiresManagerRefundApproval?: boolean;
  canSetGiftCardExpiry?: boolean;
};

function formatShiftTime(value: string | null, language: Language): string {
  if (!value) return language === "ar" ? "غير مفتوحة" : "Not open";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return language === "ar" ? "مفتوحة" : "Open";
  return parsed.toLocaleTimeString(language === "ar" ? "ar-AE" : "en-AE", {
    hour: "2-digit",
    minute: "2-digit"
  });
}

function roleInitials(roleName: string): string {
  const words = roleName.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "LY";
  if (words.length > 1) return `${words[0]?.[0] ?? ""}${words[1]?.[0] ?? ""}`.toUpperCase();
  return words[0]!.slice(0, 2).toUpperCase();
}

function queueErrorText(code: string, ar: boolean): string {
  switch (code) {
    case "OFFLINE_ACK_PENDING": return ar
      ? "أكد الخادم العملية، لكن تعذر تأكيد تحديث الطابور محليًا. أعد المزامنة لاستعادة النتيجة بالمفتاح نفسه؛ لا تُنشئ عملية بديلة."
      : "The server confirmed the operation, but local queue settlement could not be confirmed. Retry sync with the same key; do not create a replacement operation.";
    case "OFFLINE_LOCK_UNAVAILABLE": return ar
      ? "هذا المتصفح لا يدعم المزامنة الآمنة بين التبويبات. بقيت العمليات محفوظة دون إرسال؛ حدّث المتصفح الحالي أو تواصل مع المسؤول دون مسح بياناته."
      : "This browser cannot coordinate safe sync across tabs. Operations remain stored and unsent. Update this browser or contact an administrator without clearing its data.";
    case "OFFLINE_SYNC_BUSY": return ar ? "تجري المزامنة في تبويب آخر. انتظر اكتمالها ثم أعد المحاولة عند الحاجة." : "Another tab is syncing. Wait for it to finish, then retry if needed.";
    case "OFFLINE_SESSION_REQUIRED":
    case "OFFLINE_SESSION_CHANGED": return ar ? "توقفت المزامنة لتغيّر الجلسة أو انتهائها. ادخل بالحساب والفرع الأصليين؛ لم تُحذف العمليات المعلقة." : "Sync stopped because the session changed or expired. Sign in to the original account and branch; pending operations were not deleted.";
    case "OFFLINE_PERMISSION_DENIED": return ar ? "لا تسمح الصلاحيات الحالية بمزامنة هذه العمليات. بقيت محفوظة للمراجعة." : "Current permissions do not allow these operations to sync. They remain stored for review.";
    case "OFFLINE_CONFLICT":
    case "OFFLINE_REVIEW_REQUIRED": return ar ? "تحتاج العملية إلى مراجعة حالتها على الخادم قبل إعادة المحاولة. بقيت محفوظة ولم تُستبدل بياناتها." : "Review the operation's server state before retrying. It remains stored with its original input.";
    default: return ar ? "تعذر إكمال المزامنة أو قراءة التخزين المحلي. لا تمسح بيانات المتصفح؛ أعد المحاولة، واطلب المساعدة إذا استمر الخطأ." : "Sync or local storage access failed. Do not clear browser data. Retry and seek assistance if the error persists.";
  }
}

/** Clean top bar: no Batch bridges, portals, mutation observers or legacy CSS imports. */
export function CashierTopBarV2({
  language,
  branchName,
  tenantName,
  roleName,
  shiftStatus,
  shiftOpenedAt,
  closedOrderCount,
  openOrderCount,
  online,
  pendingSyncCount,
  queueRequiresReview = false,
  queueErrorCode = null,
  syncing,
  pendingApprovalCount,
  futureOrdersAvailable,
  futureOrdersEnabled,
  onOpenShift,
  onOpenClosedOrders,
  onOpenSavedOrders,
  onSync,
  onOpenFutureOrders,
  onOpenManagerApprovals,
  onToggleLanguage,
  onSwitchContext,
  onLogout,
  onPrintClosedReceipt,
  onCorrectClosedOrderCustomer,
  requiresManagerRefundApproval = false,
  canSetGiftCardExpiry = false
}: CashierTopBarV2Props) {
  const ar = language === "ar";
  const shiftOpen = shiftStatus === "Open";
  const [closedOrdersOpen, setClosedOrdersOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const openClosedOrders = () => {
    setMenuOpen(false);
    setClosedOrdersOpen(true);
    onOpenClosedOrders();
  };

  const runMenuAction = (action: () => void) => {
    setMenuOpen(false);
    action();
  };

  return (
    <>
      <header className="cashier-v2-topbar" data-testid="cashier-topbar">
        <div className="cashier-v2-topbar__brand">
          <img src={BRAND.assets.mark} alt="" aria-hidden="true" />
          <span>
            <strong>{BRAND.productName}</strong>
            <small>Cashier workspace</small>
          </span>
        </div>

        <button type="button" className="cashier-v2-topbar__branch" onClick={onSwitchContext}>
          {branchName}
        </button>

        <button type="button" data-testid={shiftOpen ? undefined : "open-shift"} className={`cashier-v2-topbar__shift${shiftOpen ? " is-open" : ""}`} onClick={onOpenShift}>
          <span className="cashier-v2-topbar__dot" aria-hidden="true" />
          <span><small>SHIFT</small><strong>{shiftOpen ? formatShiftTime(shiftOpenedAt, language) : (ar ? "غير مفتوحة" : "Not open")}</strong></span>
        </button>

        <span className="cashier-v2-topbar__grow" aria-hidden="true" />

        <nav className="cashier-v2-topbar__actions" aria-label={ar ? "اختصارات الكاشير" : "Cashier shortcuts"}>
          <button type="button" data-testid="open-closed-orders" onClick={openClosedOrders}>
            <span className="cashier-v2-topbar__count">{closedOrderCount}</span>
            <span>{ar ? "المغلقة" : "Closed"}</span>
          </button>
          <button type="button" data-testid="open-saved-orders" onClick={onOpenSavedOrders}>
            <span className="cashier-v2-topbar__count">{openOrderCount}</span>
            <span>{ar ? "المحفوظة / المفتوحة" : "Saved / Open"}</span>
          </button>
          <span className={`cashier-v2-topbar__connection${online ? " is-online" : ""}`} role="status">
            <span className="cashier-v2-topbar__dot" aria-hidden="true" />
            {online ? (ar ? "متصل" : "Online") : (ar ? "غير متصل" : "Offline")}
          </span>
          {pendingSyncCount > 0 ? (
            <button type="button" data-testid="offline-queue-sync" disabled={!online || syncing} onClick={onSync}>
              {syncing ? (ar ? "جارٍ المزامنة…" : "Syncing…") : (ar ? `مزامنة ${pendingSyncCount}` : `Sync ${pendingSyncCount}`)}
            </button>
          ) : null}
          {onOpenManagerApprovals ? (
            <button
              type="button"
              className="cashier-v2-topbar__manager"
              data-testid="cashier-manager-approvals"
              onClick={onOpenManagerApprovals}
            >
              {ar
                ? `موافقات المدير${pendingApprovalCount ? ` (${pendingApprovalCount})` : ""}`
                : `Manager Approvals${pendingApprovalCount ? ` (${pendingApprovalCount})` : ""}`}
            </button>
          ) : null}
          <button type="button" className="cashier-v2-topbar__language" onClick={onToggleLanguage}>
            {ar ? "EN" : "AR"}
          </button>
          <span className="cashier-v2-topbar__avatar" title={`${tenantName} · ${roleName}`}>{roleInitials(roleName)}</span>
          <div className={`cashier-v2-topbar__menu${menuOpen ? " is-open" : ""}`}>
            <button
              type="button"
              className="cashier-v2-topbar__menu-trigger"
              aria-label={ar ? "المزيد" : "More"}
              aria-expanded={menuOpen}
              data-testid="cashier-more-menu"
              onClick={() => setMenuOpen((current) => !current)}
            >
              •••
            </button>
            {menuOpen ? (
              <div className="cashier-v2-topbar__menu-panel">
                {!shiftOpen ? (
                  <button type="button" data-testid="open-shift-menu" onClick={() => runMenuAction(onOpenShift)}>
                    {ar ? "فتح الوردية" : "Open shift"}
                  </button>
                ) : null}
                {futureOrdersAvailable ? (
                  <button type="button" data-testid="future-orders-button" disabled={!futureOrdersEnabled} onClick={() => runMenuAction(onOpenFutureOrders)}>
                    {futureOrdersEnabled ? (ar ? "الطلبات المستقبلية" : "Future orders") : (ar ? "الطلبات المستقبلية (غير مفعلة)" : "Future orders (disabled)")}
                  </button>
                ) : null}
                {onOpenManagerApprovals ? (
                  <button type="button" data-testid="cashier-manager-approvals-menu" onClick={() => runMenuAction(onOpenManagerApprovals)}>
                    {ar
                      ? `موافقات المدير${pendingApprovalCount ? ` (${pendingApprovalCount})` : ""}`
                      : `Manager Approvals${pendingApprovalCount ? ` (${pendingApprovalCount})` : ""}`}
                  </button>
                ) : null}
                <button type="button" onClick={() => runMenuAction(onSwitchContext)}>{ar ? "تغيير الفرع" : "Switch branch"}</button>
                <button type="button" className="is-danger" onClick={() => runMenuAction(onLogout)}>{ar ? "تسجيل الخروج" : "Log out"}</button>
              </div>
            ) : null}
          </div>
        </nav>
      </header>

      {queueRequiresReview ? (
        <div data-testid="offline-queue-review">
          <Alert tone="warning" title={ar ? "عمليات محفوظة تحتاج مراجعة مسؤول" : "Stored operations need administrator review"}>
            {ar
              ? "لم تُرسل هذه العمليات لأن سياقها أو عقدها غير مثبت. لا تمسح بيانات المتصفح ولا تعِد إسنادها إلى الحساب الحالي."
              : "These operations were not sent because their context or contract is unverified. Do not clear browser data or assign them to the current account."}
          </Alert>
        </div>
      ) : null}
      {queueErrorCode ? (
        <div data-testid="offline-queue-error">
          <Alert tone="warning" title={ar ? "تنبيه المزامنة" : "Sync notice"}>
            {queueErrorText(queueErrorCode, ar)}
          </Alert>
          <button type="button" data-testid="offline-queue-retry" disabled={!online || syncing} onClick={onSync}>
            {ar ? "إعادة فحص المزامنة" : "Retry queue sync"}
          </button>
        </div>
      ) : null}

      <CashierClosedOrdersDrawer
        language={language}
        open={closedOrdersOpen}
        onClose={() => setClosedOrdersOpen(false)}
        onPrintReceipt={onPrintClosedReceipt}
        onCorrectCustomer={onCorrectClosedOrderCustomer ? (closedWorkflow) => {
          setClosedOrdersOpen(false);
          onCorrectClosedOrderCustomer(closedWorkflow);
        } : undefined}
        requiresManagerRefundApproval={requiresManagerRefundApproval}
        canSetGiftCardExpiry={canSetGiftCardExpiry}
        onOpenManagerApprovals={onOpenManagerApprovals ? () => {
          setClosedOrdersOpen(false);
          onOpenManagerApprovals();
        } : undefined}
      />
    </>
  );
}
